/**
 * A chamada como JANELA — tela cheia com chat, ou miniatura flutuante.
 *
 * **O que o Augusto pediu (07/08/2026).** A chamada ocupava uma faixa de 38%
 * da altura, empurrando o atendimento para baixo: *"a tela da chamada ficou em
 * cima da tela de atendimento. O layout não está legal, definitivamente não
 * está legal."*
 *
 * A referência que ele deu é o Bitrix24: *"ele cria de fato um modal em cima da
 * tela inteira com a parte de chat da chamada … e aí você, se quiser, pode
 * minimizar e ele fica pequenininho, como um Picture in Picture, porque aí você
 * fica fazendo outras coisas enquanto está na chamada."*
 *
 * ## A decisão que sustenta o componente
 *
 * Minimizar **não pode desmontar a sala**. Se os dois modos fossem dois ramos
 * de JSX diferentes, o React destruiria o `<InlineCallRoom>` de um e criaria
 * outro do zero — na prática, sair e voltar à chamada a cada clique, com perda
 * de áudio, novo handshake e um susto no outro lado.
 *
 * Por isso o `<InlineCallRoom>` fica SEMPRE na mesma posição da árvore, e só as
 * classes do invólucro mudam. O chat é que aparece e some, e isso é inofensivo:
 * ele relê o histórico do servidor ao montar.
 *
 * O mesmo cuidado explica o cronômetro estar em `DuracaoDaChamada`: estado que
 * muda por tempo no componente que hospeda a sala re-renderiza a sala. Foi
 * exatamente isso que derrubou a chamada em produção antes desta correção.
 *
 * ## Os avisos (A12, 25/10/2026)
 *
 * O antigo `CallModal` avisava por toast "Câmera ou microfone bloqueado", "Reconectando…" e "Conexão
 * restaurada.". A janela é `aria-modal` em tela cheia, então o aviso de uma superfície aparece NELA: uma
 * `PonteDaSala` (filha da sala, como na janelinha de voz) entrega a conexão e o erro de dispositivo, e a janela
 * desenha UMA linha em dois tons — alerta vermelho para o erro, status escuro para o resto. O "Conexão WebRTC
 * perdida." do antigo não volta: a queda definitiva fecha a janela (`onDisconnected={onEnd}`).
 */
import { Expand, Loader2, Maximize2, Minimize2, PhoneOff, Shrink } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { Button } from "@/components/ui/button";
import { useErroTemporario } from "@/hooks/useErroTemporario";
import { cn } from "@/lib/utils";

import { DuracaoDaChamada } from "./DuracaoDaChamada";
import { InlineCallRoom } from "./InlineCallRoom";
import { PonteDaSala, type EstadoDaSala } from "./PonteDaSala";

export type ModoDaJanela = "cheia" | "miniatura";

/** O texto fixo do erro de dispositivo no vídeo (a voz fala só de microfone): nunca a mensagem do navegador. */
const ERRO_DE_DISPOSITIVO_DO_VIDEO =
  "Câmera ou microfone bloqueado. Libere nas permissões do navegador.";
/** Quanto o "Conexão restaurada." fica na tela. */
const CONEXAO_RESTAURADA_MS = 3_000;

/** Onde a sala está, como a ponte conta: `fora` = ainda não há sala (ou ela saiu do ar). */
type FaseDaSala = "fora" | "conectando" | "conectada" | "reconectando";

const faseDaSala = (estado: EstadoDaSala | null): FaseDaSala =>
  !estado
    ? "fora"
    : estado.reconectando
      ? "reconectando"
      : estado.conectada
        ? "conectada"
        : "conectando";

export interface JanelaDeChamadaProps {
  /** Dados da sala. `null` enquanto o servidor ainda não respondeu. */
  chamada: { callSessionId: string; token: string; wsUrl: string } | null;
  /** ISO 8601 do início — alimenta o cronômetro. */
  startedAt: string;
  /** Encerrar de vez (botão vermelho, ou queda da conexão). */
  onEnd: () => void;
  /** O chat do atendimento, renderizado ao lado do vídeo em tela cheia. */
  chat?: ReactNode;
  /** Renderizado DENTRO do contexto da sala — quem precisa de
   *  `useRoomContext()` para publicar mídia (o gate de compartilhamento de
   *  tela do portal, por exemplo). */
  children?: ReactNode;
  /** Título da conversa, no cabeçalho. */
  titulo?: string;
  /** Modo atual. Controlado PELA PÁGINA, não por este componente.
   *
   *  Precisa ser assim porque em tela cheia a página não deve renderizar o
   *  chat de fundo: o de dentro da janela já está lá, e dois `<SessionChat>`
   *  montados ao mesmo tempo significam duas assinaturas e dois laços de
   *  atualização para a mesma conversa. Quem decide o que desenhar no fundo é
   *  a página, então é ela que precisa conhecer o modo. */
  modo: ModoDaJanela;
  onModoChange: (modo: ModoDaJanela) => void;
}

export function JanelaDeChamada({
  chamada,
  startedAt,
  onEnd,
  chat,
  titulo,
  modo,
  onModoChange,
  children,
}: JanelaDeChamadaProps) {
  // O erro passa sozinho em 8 s. Identidade estável: vai como `onErro` para dentro da sala e da ponte.
  const { erro, definirErro } = useErroTemporario();
  // O "Conexão restaurada." usa o mesmo temporizador, em 3 s: é um texto que passa sozinho, só que não é erro.
  const {
    erro: restaurada,
    definirErro: avisarRestaurada,
    limparErro: limparRestaurada,
  } = useErroTemporario(CONEXAO_RESTAURADA_MS);
  // Só a FASE sobe da ponte: microfone e contagem de gente mudam o estado dela mas não a fase, e `setState` com
  // o mesmo valor não re-renderiza a janela (que hospeda a sala).
  const [fase, setFase] = useState<FaseDaSala>("fora");
  const aoMudarASala = useCallback(
    (estado: EstadoDaSala | null) => setFase(faseDaSala(estado)),
    []
  );
  const cheia = modo === "cheia";
  const caixa = useRef<HTMLDivElement>(null);

  /* ------------------------------------------------------------------
   * Tela cheia DE VERDADE — a que esconde a barra do navegador.
   *
   * **O pedido do Augusto (08/08/2026).** No celular: *"seria interessante se
   * a parte do vídeo pudesse ocupar a área da tela inteira do celular, meio
   * que entrar em modo tela cheia … e se a pessoa quiser minimizar, volta do
   * jeito que está hoje."*
   *
   * São TRÊS coisas diferentes, e confundi-las é o erro comum:
   *
   * - `inset-0` cobre o LAYOUT viewport, que no Android é mais alto que a área
   *   visível enquanto a barra de URL está na tela;
   * - `100dvh` acompanha essa barra, então o fundo do contêiner é o fundo
   *   VISÍVEL — é o que impede o botão de encerrar de sair cortado;
   * - a Fullscreen API esconde a barra, e só ela dá a tela inteira.
   *
   * O botão só aparece onde a API existe. Nada de assumir suporte: hoje mesmo
   * eu quebrei a troca de câmera afirmando comportamento de navegador sem
   * medir. Aqui a detecção decide, e onde não houver, o `dvh` já entrega a
   * área visível inteira.
   * ------------------------------------------------------------------ */
  const [emTelaCheia, setEmTelaCheia] = useState(false);
  const suportaTelaCheia =
    typeof document !== "undefined" &&
    typeof document.documentElement?.requestFullscreen === "function";

  useEffect(() => {
    const aoMudar = () => setEmTelaCheia(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", aoMudar);
    return () => document.removeEventListener("fullscreenchange", aoMudar);
  }, []);

  const alternarTelaCheia = useCallback(async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await caixa.current?.requestFullscreen?.();
      }
    } catch {
      // Gesto recusado ou API bloqueada por política de permissões. Sem
      // alarde: a janela continua ocupando a área visível pelo `dvh`.
    }
  }, []);

  // Minimizar durante a tela cheia precisa SAIR dela — senão a miniatura
  // ficaria sozinha num fundo preto ocupando o aparelho inteiro.
  const irParaMiniatura = useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    onModoChange("miniatura");
  }, [onModoChange]);

  // "Conexão restaurada." só vale DEPOIS de uma queda: conectar pela primeira vez não é restaurar nada, e a sala que
  // sai do ar zera a queda (a próxima não herda).
  const caiu = useRef(false);
  useEffect(() => {
    if (fase === "reconectando") {
      caiu.current = true;
      limparRestaurada(); // caiu de novo antes dos 3 s: o "Reconectando…" volta e a restaurada antiga não reaparece
    } else if (fase === "conectada" && caiu.current) {
      caiu.current = false;
      avisarRestaurada("Conexão restaurada.");
    } else if (fase === "fora") {
      caiu.current = false;
      limparRestaurada();
    }
  }, [fase, avisarRestaurada, limparRestaurada]);

  // A linha de aviso: o erro tem prioridade (uma linha só), depois o "Reconectando…" (enquanto durar), depois a
  // restaurada (3 s). Em cima na tela cheia, no slot que o erro sempre teve; na miniatura o TOPO é dos controles,
  // então a linha desce para o pé e encurta (`truncate`).
  const aviso = fase === "reconectando" ? "Reconectando…" : restaurada;
  const lugarDaLinha = cheia ? "inset-x-3 top-3 text-xs" : "inset-x-2 bottom-2 text-[11px]";

  // Portal no document.body: desde 25/09/2026 o painel de atendimento mora numa aba de
  // /mensagens e fica `display:none` quando a pessoa vai para "Interno". Dentro dele,
  // esta janela `fixed` sumiria junto — a chamada seguiria no ar, sem o botão de encerrar.
  // O alvo do portal é fixo, então a `<InlineCallRoom>` não remonta.
  return createPortal(
    <div
      className={cn(
        // `pointer-events-auto`: com um Dialog modal aberto o Radix põe `pointer-events: none` no `body`, e esta
        // janela (portal no `body`) herdaria isso: o clique atravessaria até a sobreposição do Dialog e o fecharia,
        // com o `stopPropagation` da raiz sem chance de agir. A janelinha de voz tem a mesma classe.
        "pointer-events-auto fixed z-50 flex flex-col overflow-hidden bg-zinc-900 text-zinc-100 shadow-2xl",
        cheia
          ? // `inset-x-0 top-0` + `100dvh` em vez de `inset-0`: aquele ancora no
            // LAYOUT viewport, que no Android é mais alto que a área visível
            // enquanto a barra de URL está na tela — e o botão de encerrar saía
            // medido a partir de um fundo FORA DA TELA. Mesma correção já
            // aprendida no `CameraGuide` do Lotese.
            "inset-x-0 top-0"
          : cn(
              // Miniatura: acima do conteúdo, sem cobrir a tela. É o modo
              // "faça outra coisa enquanto fala".
              "rounded-xl border border-zinc-700",
              // No CELULAR ela vai para o ALTO. Ficava embaixo, à direita — e
              // ali é exatamente onde mora a caixa de texto do chat, que ocupa
              // a largura inteira da faixa inferior. O Augusto: *"quando eu
              // minimizo, ele acaba ficando em cima dos botões e da caixa de
              // texto do chat"*. A miniatura existe para deixar a pessoa fazer
              // outra coisa durante a chamada; tapar o campo de digitar é
              // impedir a principal dessas coisas.
              //
              // Menor no celular também: 320px de largura numa tela de 390
              // deixaria 70px de conversa visível ao lado.
              // `env(safe-area-inset-top)` desce a miniatura abaixo do entalhe
              // e da barra de status no iPhone. No Android ele devolve 0 — o
              // inset existe para o entalhe, não para a barra de gestos — então
              // ali sobra só os 0.75rem. Mesma observação registrada no
              // `CameraGuide` do Lotese.
              "right-3 top-[calc(0.75rem+env(safe-area-inset-top,0px))] h-32 w-48",
              // No desktop, o canto inferior direito — que é a convenção de
              // imagem-em-imagem e onde há espaço de sobra.
              "lg:bottom-4 lg:right-4 lg:top-auto lg:h-56 lg:w-80"
            )
      )}
      ref={caixa}
      style={cheia ? { height: "100dvh" } : undefined}
      role="dialog"
      aria-modal={cheia}
      aria-label="Chamada em andamento"
      data-testid="janela-de-chamada"
      data-modo={modo}
      // A janela é um portal FORA da árvore React de qualquer Dialog/Sheet aberto: sem isto o Radix toma o aperto
      // por "clicou fora" e fecha o formulário que a pessoa estava preenchendo (o mesmo cuidado da janelinha de voz
      // e do `AvisoDeChamada`). O React escuta no contêiner do portal (`document.body`), então parar aqui impede o
      // ouvinte do `document`. Os cliques seguem funcionando: só o `pointerdown` é contido.
      // Duas condições. (1) Só com uma camada MODAL aberta: o Radix trava o `body` (`pointer-events: none`) enquanto
      // há uma; sem ela, o clique no vídeo, no cabeçalho ou no fundo precisa chegar ao `document` para fechar os
      // Popovers não modais do chat desta janela (TagPicker, TemplatePicker). (2) Só o que cai DENTRO da janela no
      // DOM: o que sobe por filhos em portal (a sobreposição de um Dialog aberto de dentro do chat, como o de
      // transferir) tem o alvo fora dela e precisa chegar ao `document`, por onde o Radix deles fecha no clique fora.
      onPointerDown={(evento) => {
        if (
          document.body.style.pointerEvents === "none" &&
          evento.currentTarget.contains(evento.target as Node)
        )
          evento.stopPropagation();
      }}
    >
      {/* Cabeçalho — some na miniatura, onde cada pixel conta. */}
      {cheia && (
        <header className="flex shrink-0 items-center gap-3 border-b border-zinc-800 px-4 py-2">
          {/* O título some no celular. No print do Augusto ele empurrava os
              botões e o "Encerrar" saía CORTADO na borda direita. Numa largura
              de 412px, saber que se está numa chamada é óbvio; conseguir
              desligar não. */}
          <span className="hidden truncate text-sm font-medium sm:inline">
            {titulo || "Chamada"}
          </span>
          <DuracaoDaChamada
            startedAt={startedAt}
            className="rounded bg-black/40 px-2 py-0.5 text-xs tabular-nums"
          />
          <div className="ml-auto flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={irParaMiniatura}
              data-testid="minimizar-chamada"
              aria-label="Minimizar chamada"
            >
              <Minimize2 className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Minimizar
            </Button>
            {suportaTelaCheia && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={alternarTelaCheia}
                data-testid="alternar-tela-cheia"
                aria-label={emTelaCheia ? "Sair da tela cheia" : "Ocupar a tela inteira"}
              >
                {emTelaCheia ? (
                  <Shrink className="h-4 w-4 sm:mr-1.5" aria-hidden="true" />
                ) : (
                  <Expand className="h-4 w-4 sm:mr-1.5" aria-hidden="true" />
                )}
                <span className="hidden sm:inline">{emTelaCheia ? "Sair" : "Tela cheia"}</span>
              </Button>
            )}

            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={onEnd}
              data-testid="end-call-button"
            >
              <PhoneOff className="h-4 w-4 sm:mr-1.5" aria-hidden="true" />
              <span className="hidden sm:inline">Encerrar chamada</span>
            </Button>
          </div>
        </header>
      )}

      <div className={cn("flex min-h-0 flex-1", cheia && "flex-row")}>
        {/* ⚠️ POSIÇÃO FIXA NA ÁRVORE.
            Este bloco é o mesmo elemento nos dois modos — só as classes mudam.
            Movê-lo para dentro de um ramo condicional faria o React desmontar a
            sala ao minimizar, derrubando a chamada a cada clique. */}
        <div className={cn("relative min-w-0 flex-1", cheia && chat && "lg:flex-[2]")}>
          {chamada ? (
            <InlineCallRoom
              token={chamada.token}
              wsUrl={chamada.wsUrl}
              onEnd={onEnd}
              onDisconnected={onEnd}
              onErro={definirErro}
              // A barra completa só na tela cheia — ver `mostrarControles`.
              mostrarControles={cheia}
              // Na miniatura, só o palco: uma faixa de rostos numa caixa de
              // 192×128 não caberia — e sobreposição ali já quebrou os botões
              // duas vezes.
              compacto={!cheia}
              className="h-full w-full"
            >
              {/* A ponte entrega a conexão e o erro de dispositivo para os avisos desta janela. Não desenha nada;
                  fica ao lado do que a página põe dentro da sala (o gate de tela do portal). */}
              <PonteDaSala
                onEstado={aoMudarASala}
                onErro={definirErro}
                textoDoErroDeDispositivo={ERRO_DE_DISPOSITIVO_DO_VIDEO}
              />
              {children}
            </InlineCallRoom>
          ) : (
            <div
              className="flex h-full w-full items-center justify-center text-sm"
              data-testid="chamada-conectando"
            >
              Conectando à chamada…
            </div>
          )}

          {erro ? (
            <div
              role="alert"
              data-testid="erro-da-janela-de-video"
              title={erro}
              className={cn(
                "absolute z-30 rounded bg-red-900/90 px-2 py-1",
                lugarDaLinha,
                !cheia && "truncate"
              )}
            >
              {erro}
            </div>
          ) : (
            aviso && (
              <div
                role="status"
                aria-live="polite"
                data-testid="aviso-da-janela-de-video"
                className={cn(
                  "absolute z-30 flex items-center gap-1.5 rounded bg-black/60 px-2 py-1 text-zinc-100",
                  lugarDaLinha
                )}
              >
                {fase === "reconectando" && (
                  <Loader2
                    className="h-3.5 w-3.5 shrink-0 animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                )}
                <span className={cn("min-w-0", !cheia && "truncate")} title={aviso}>
                  {aviso}
                </span>
              </div>
            )
          )}

          {/* Na miniatura os controles do cabeçalho não existem — estes ficam
              sobrepostos ao vídeo, que é o padrão de todo PiP. */}
          {!cheia && (
            <div
              data-testid="controles-da-miniatura"
              className="absolute inset-x-0 top-0 z-30 flex items-center gap-1.5 bg-gradient-to-b from-black/80 to-transparent px-2 py-1.5"
            >
              <DuracaoDaChamada
                startedAt={startedAt}
                className="rounded bg-black/50 px-1.5 py-0.5 text-[11px] tabular-nums"
              />
              <div className="ml-auto flex items-center gap-1">
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  // 40px. Antes eram 28 — abaixo do alvo de toque confortável
                  // (~44px de recomendação), e o dedo errava. Com a barra
                  // completa escondida, sobra espaço de sobra para isto.
                  className="h-10 w-10"
                  onClick={() => onModoChange("cheia")}
                  data-testid="expandir-chamada"
                  aria-label="Expandir chamada"
                >
                  <Maximize2 className="h-5 w-5" aria-hidden="true" />
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="h-10 w-10"
                  onClick={onEnd}
                  data-testid="end-call-button-mini"
                  aria-label="Encerrar chamada"
                >
                  <PhoneOff className="h-5 w-5" aria-hidden="true" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* O chat ao lado do vídeo — o pedido explícito ("realmente ele cria uma
            chamada com chat"). Só em tela cheia: na miniatura não caberia, e
            espremê-lo ali seria pior que não tê-lo. */}
        {cheia && chat && (
          <aside
            className="hidden min-w-0 flex-1 flex-col border-l border-zinc-800 bg-background text-foreground lg:flex lg:max-w-md"
            data-testid="chat-da-chamada"
          >
            {chat}
          </aside>
        )}
      </div>
    </div>,
    document.body
  );
}
